import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Settings2, QrCode, BellRing, BellOff, Languages, Loader2, BadgePercent, Timer } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { EnabledLanguagesPicker } from '../../components/shared/EnabledLanguagesPicker';
import { RecipientListEditor } from '../../components/shared/RecipientListEditor';
import { QrCodeModal } from '../../components/cards/QrCodeModal';
import { cardsAPI, vehicleBroadcastAPI, vehicleSettingsAPI, vehiclesAPI } from '../../lib/api';
import { useAuth } from '../../lib/auth';

/**
 * Command Center → vehicle detail → "Manage".
 *
 * A second access point to data that is ALREADY editable elsewhere (Cards page,
 * Used Vehicles → Showroom Settings, Used Vehicles → Alert list). Every write
 * below goes through an existing endpoint, and every one of those endpoints
 * already requires SUPER_ADMIN / CLIENT_ADMIN / LOCATION_MANAGER — the same
 * roles that can open this dialog at all — so there is no extra gate here.
 *
 * Portal-wide: nothing is special-cased to the demo org. The demo Manager is
 * simply a real client_admin scoped to one org, so it lands here like any other.
 */

const SHOWROOM_TOGGLES = [
    ['fi_financing_enabled', 'fi.toggleFinancing', 'Financing'],
    ['fi_warranty_enabled', 'fi.toggleWarranty', 'Extended warranty'],
    ['fi_insurance_enabled', 'fi.toggleInsurance', 'Insurance quotes'],
    ['fi_trade_in_enabled', 'fi.toggleTradeIn', 'Trade-in appraisals'],
    ['action_noise_enabled', 'tactile.orgToggle', 'Buyer action sounds'],
    ['scan_alert_cooldown_enabled', 'scanCooldown.orgToggle', 'Limit scan alerts to one per vehicle every 15 minutes'],
    ['buyer_activity_visible_to_salespeople', 'buyerActivity.orgToggle', 'Buyer Activity visible to salespeople'],
];

export default function VehicleManageSection({ vehicle, card, onSaved }) {
    const { t } = useTranslation();
    const { user } = useAuth();
    const orgId = vehicle?.organization_id;

    // Only landing-settings / enabled-languages exclude LOCATION_MANAGER, so
    // the language control is disabled for them rather than silently 403-ing.
    const canEditLanguages = ['super_admin', 'client_admin'].includes(user?.role);

    const [open, setOpen] = useState(false);
    const [qrOpen, setQrOpen] = useState(false);
    const [busy, setBusy] = useState(false);

    const [langs, setLangs] = useState(undefined);       // undefined = not loaded
    const [bc, setBc] = useState(null);
    const [bcPhonesOk, setBcPhonesOk] = useState(true);
    const [settings, setSettings] = useState(null);
    // Tri-state per-vehicle override: null = inherit the dashboard toggle.
    const [payEstOverride, setPayEstOverride] = useState(null);
    // Tri-state per-vehicle override: null = inherit the dealership default (ON).
    const [scanCdOverride, setScanCdOverride] = useState(null);

    const load = useCallback(async () => {
        setBusy(true);
        try {
            const [bcR, stR] = await Promise.all([
                vehicleBroadcastAPI.get(vehicle.id),
                vehicleSettingsAPI.get(orgId),
            ]);
            setBc({
                rows: bcR.data.notification_phones || [],
                uses_default: bcR.data.uses_default,
                muted: bcR.data.muted,
                org_default: bcR.data.org_default || [],
                max: bcR.data.max_recipients || 10,
            });
            setSettings({
                buyer_theme: stR.data?.buyer_theme || 'dark',
                fi_financing_enabled: stR.data?.fi_financing_enabled !== false,
                fi_warranty_enabled: stR.data?.fi_warranty_enabled !== false,
                fi_insurance_enabled: stR.data?.fi_insurance_enabled !== false,
                fi_trade_in_enabled: stR.data?.fi_trade_in_enabled !== false,
                action_noise_enabled: stR.data?.action_noise_enabled !== false,
                // Default OFF — matches the org-level default for this field.
                payment_estimate_enabled: stR.data?.payment_estimate_enabled === true,
                // Default ON — one scan alert per vehicle per 15 minutes.
                scan_alert_cooldown_enabled: stR.data?.scan_alert_cooldown_enabled !== false,
                // Default ON — managers always see it either way.
                buyer_activity_visible_to_salespeople: stR.data?.buyer_activity_visible_to_salespeople !== false,
            });
            setLangs(Array.isArray(card?.enabled_languages) ? card.enabled_languages : null);
            setPayEstOverride(
                typeof vehicle?.payment_estimate_enabled === 'boolean' ? vehicle.payment_estimate_enabled : null);
            setScanCdOverride(
                typeof vehicle?.scan_alert_cooldown_enabled === 'boolean' ? vehicle.scan_alert_cooldown_enabled : null);
        } catch (e) {
            toast.error(e.response?.data?.detail || t('common.error', 'Something went wrong'));
        } finally { setBusy(false); }
    }, [vehicle?.id, vehicle?.payment_estimate_enabled, vehicle?.scan_alert_cooldown_enabled, orgId, card, t]);

    useEffect(() => { if (open && !settings) load(); }, [open, settings, load]);

    const saveAll = async () => {
        setBusy(true);
        try {
            await vehicleBroadcastAPI.update(vehicle.id, {
                notification_phones: bc.uses_default ? null : bc.rows.filter((r) => (r.phone || '').trim()),
                use_default: bc.uses_default, muted: bc.muted,
            });
            await vehicleSettingsAPI.update(orgId, settings);
            // Tri-state: null clears the override so this vehicle re-inherits.
            await vehiclesAPI.update(vehicle.id, {
                payment_estimate_enabled: payEstOverride,
                scan_alert_cooldown_enabled: scanCdOverride,
            });
            if (canEditLanguages && card?.id) {
                await cardsAPI.updateEnabledLanguages(card.id, langs);
            }
            toast.success(t('ccManage.saved', 'Changes saved'));
            await onSaved?.();
            setSettings(null);      // force a reload next open
            setOpen(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || t('common.error', 'Something went wrong'));
        } finally { setBusy(false); }
    };

    if (!vehicle?.id) return null;

    return (
        <div className="rounded-xl border border-slate-700 bg-slate-900/40 p-3" data-testid="cc-manage-section">
            <button type="button" onClick={() => setOpen((o) => !o)}
                className="w-full flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-300 hover:text-white transition-colors"
                data-testid="cc-manage-toggle" aria-expanded={open}>
                <Settings2 className="w-3.5 h-3.5" />
                {t('ccManage.title', 'Manage')}
                <span className="ml-auto text-[10px] font-normal normal-case text-slate-500">
                    {open ? t('common.hide', 'Hide') : t('common.show', 'Show')}
                </span>
            </button>

            {open && (
                <div className="mt-3 space-y-4">
                    {busy && !settings ? (
                        <div className="flex items-center gap-2 text-xs text-slate-400" data-testid="cc-manage-loading">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />{t('common.loading', 'Loading...')}
                        </div>
                    ) : settings && (
                        <>
                            {/* ── CARD: languages + QR ── */}
                            <div className="space-y-2" data-testid="cc-manage-card">
                                <Label className="text-slate-200 text-xs flex items-center gap-1.5">
                                    <Languages className="w-3.5 h-3.5" />{t('ccManage.cardTitle', 'Tap card')}
                                </Label>
                                {!card?.id ? (
                                    <p className="text-[11px] text-slate-500" data-testid="cc-manage-nocard">
                                        {t('ccManage.noCard', 'No tap card is assigned to this vehicle yet.')}
                                    </p>
                                ) : (
                                    <>
                                        <p className="text-[11px] text-slate-400" data-testid="cc-manage-card-id">
                                            #{card.card_id}
                                        </p>
                                        <EnabledLanguagesPicker
                                            value={langs} allowInherit disabled={!canEditLanguages}
                                            onChange={setLangs} testIdPrefix="cc-manage-languages"
                                        />
                                        {!canEditLanguages && (
                                            <p className="text-[11px] text-amber-400/80" data-testid="cc-manage-lang-locked">
                                                {t('ccManage.langLocked', 'Only an account admin can change card languages.')}
                                            </p>
                                        )}
                                        <Button size="sm" variant="outline" className="w-full"
                                            onClick={() => setQrOpen(true)} data-testid="cc-manage-qr-btn">
                                            <QrCode className="w-3.5 h-3.5 mr-1.5" />
                                            {t('ccManage.qr', 'View / download QR code')}
                                        </Button>
                                    </>
                                )}
                            </div>

                            {/* ── NOTIFICATIONS: per-vehicle broadcast list ── */}
                            <div className="space-y-2 pt-3 border-t border-slate-800" data-testid="cc-manage-notifications">
                                <Label className="text-slate-200 text-xs flex items-center gap-1.5">
                                    {bc?.muted ? <BellOff className="w-3.5 h-3.5" /> : <BellRing className="w-3.5 h-3.5 text-emerald-500" />}
                                    {t('broadcast.title', 'Who gets texted about this vehicle')}
                                </Label>
                                <label className="flex items-start gap-2 text-sm cursor-pointer text-slate-300">
                                    <Checkbox checked={bc.muted} onCheckedChange={(c) => setBc((p) => ({ ...p, muted: !!c }))}
                                        data-testid="cc-manage-mute" />
                                    <span>
                                        <span className="font-medium">{t('broadcast.mute', 'Mute this vehicle')}</span>
                                        <span className="block text-[11px] text-slate-500">{t('broadcast.muteHint', 'Stops scan and new-enquiry texts. A conversation someone already owns keeps notifying them.')}</span>
                                    </span>
                                </label>
                                <label className="flex items-start gap-2 text-sm cursor-pointer text-slate-300">
                                    <Checkbox checked={bc.uses_default} disabled={bc.muted}
                                        onCheckedChange={(c) => setBc((p) => ({ ...p, uses_default: !!c }))}
                                        data-testid="cc-manage-use-default" />
                                    <span>
                                        <span className="font-medium">{t('broadcast.useDefault', 'Use the dealership default list')}</span>
                                        <span className="block text-[11px] text-slate-500">
                                            {(bc.org_default || []).map((r) => r.name || r.phone).join(', ') || t('broadcast.noDefault', 'No default list set yet')}
                                        </span>
                                    </span>
                                </label>
                                {!bc.uses_default && (
                                    <RecipientListEditor
                                        value={bc.rows} max={bc.max} disabled={bc.muted}
                                        onChange={(rows) => setBc((p) => ({ ...p, rows }))}
                                        onValidityChange={setBcPhonesOk}
                                        testid="cc-manage-recipients"
                                    />
                                )}
                            </div>

                            {/* ── SHOWROOM TOGGLES (org-wide) ── */}
                            <div className="space-y-2 pt-3 border-t border-slate-800" data-testid="cc-manage-showroom">
                                <Label className="text-slate-200 text-xs">{t('ccManage.showroomTitle', 'Showroom settings')}</Label>
                                <p className="text-[11px] text-slate-500">{t('ccManage.showroomHint', 'These apply to every vehicle in this dealership, not just this one.')}</p>
                                <div className="grid grid-cols-2 gap-2" data-testid="cc-manage-buyer-theme">
                                    {[['dark', t('buyerTheme.dark', 'Night (dark)')], ['light', t('buyerTheme.light', 'Day (light)')]].map(([key, label]) => (
                                        <button key={key} type="button" onClick={() => setSettings((s) => ({ ...s, buyer_theme: key }))}
                                            data-testid={`cc-manage-theme-${key}`}
                                            className={`h-9 rounded-lg border text-xs font-semibold transition-colors ${(settings.buyer_theme || 'dark') === key ? 'border-sky-500 bg-sky-500/10 text-white' : 'border-slate-700 bg-slate-900 text-slate-400 hover:bg-slate-800'}`}>
                                            {label}
                                        </button>
                                    ))}
                                </div>
                                {SHOWROOM_TOGGLES.map(([key, k, fallback]) => (
                                    <label key={key} className="flex items-center justify-between gap-3 rounded-lg border border-slate-700 px-3 py-2 cursor-pointer">
                                        <span className="text-xs text-slate-300">{t(k, fallback)}</span>
                                        <Switch checked={!!settings[key]} onCheckedChange={(c) => setSettings((s) => ({ ...s, [key]: c }))}
                                            data-testid={`cc-manage-${key.replace(/_/g, '-')}`} />
                                    </label>
                                ))}
                            </div>

                            {/* ── PER-VEHICLE EXCEPTION: scan-alert cooldown ──
                                Same tri-state shape as the payment estimate
                                below. OFF is the event case: many DIFFERENT
                                people tapping one windshield tag in a short
                                window, where every tap is a real lead. */}
                            <div className="space-y-2 pt-3 border-t border-slate-800" data-testid="cc-manage-scancd-override">
                                <Label className="text-slate-200 text-xs flex items-center gap-1.5">
                                    <Timer className="w-3.5 h-3.5" />
                                    {t('scanCooldown.overrideTitle', 'Scan alert frequency — this vehicle')}
                                </Label>
                                <label className="flex items-start gap-2 text-sm cursor-pointer text-slate-300">
                                    <input type="radio" className="mt-1 accent-sky-500" name="scancd-override"
                                        checked={scanCdOverride === null} onChange={() => setScanCdOverride(null)}
                                        data-testid="cc-manage-scancd-inherit-radio" />
                                    <span>
                                        <span className="font-medium">{t('scanCooldown.useDealership', 'Follow the dealership setting')}</span>
                                        <span className="block text-[11px] text-slate-500">
                                            {settings.scan_alert_cooldown_enabled
                                                ? t('scanCooldown.dealershipOn', 'Currently one alert per vehicle every 15 minutes')
                                                : t('scanCooldown.dealershipOff', 'Currently alerting on every single tap')}
                                        </span>
                                    </span>
                                </label>
                                <label className="flex items-start gap-2 text-sm cursor-pointer text-slate-300">
                                    <input type="radio" className="mt-1 accent-sky-500" name="scancd-override"
                                        checked={scanCdOverride !== null}
                                        onChange={() => setScanCdOverride(!settings.scan_alert_cooldown_enabled)}
                                        data-testid="cc-manage-scancd-override-radio" />
                                    <span>
                                        <span className="font-medium">{t('scanCooldown.overrideOnly', 'Override for this vehicle only')}</span>
                                        <span className="block text-[11px] text-slate-500">
                                            {t('scanCooldown.overrideHint', 'For a show or an event: turn the limit off so every person who taps this car alerts the desk.')}
                                        </span>
                                    </span>
                                </label>
                                {scanCdOverride !== null && (
                                    <label className="flex items-center justify-between gap-3 rounded-lg border border-sky-700/60 bg-sky-500/5 px-3 py-2 cursor-pointer ml-6">
                                        <span className="text-xs text-slate-300">
                                            {scanCdOverride
                                                ? t('scanCooldown.forceOn', 'Limit to one alert every 15 minutes')
                                                : t('scanCooldown.forceOff', 'Alert on every tap (no limit)')}
                                        </span>
                                        <Switch checked={scanCdOverride} onCheckedChange={setScanCdOverride}
                                            data-testid="cc-manage-scancd-override-switch" />
                                    </label>
                                )}
                            </div>

                            {/* ── PER-VEHICLE EXCEPTION to the dashboard toggle ──
                                Deliberately NOT a peer of the switches above: the
                                main control lives on the Command Center dashboard,
                                and this only exists to carve out one vehicle. */}
                            <div className="space-y-2 pt-3 border-t border-slate-800" data-testid="cc-manage-payest-override">
                                <Label className="text-slate-200 text-xs flex items-center gap-1.5">
                                    <BadgePercent className="w-3.5 h-3.5" />
                                    {t('ccPayEst.overrideTitle', 'Estimated monthly payment — this vehicle')}
                                </Label>
                                <label className="flex items-start gap-2 text-sm cursor-pointer text-slate-300">
                                    <input type="radio" className="mt-1 accent-sky-500" name="payest-override"
                                        checked={payEstOverride === null} onChange={() => setPayEstOverride(null)}
                                        data-testid="cc-manage-payest-inherit-radio" />
                                    <span>
                                        <span className="font-medium">{t('ccPayEst.useDashboard', 'Follow the dashboard setting')}</span>
                                        <span className="block text-[11px] text-slate-500">
                                            {settings.payment_estimate_enabled
                                                ? t('ccPayEst.dashboardOn', 'Currently showing on all vehicle pages')
                                                : t('ccPayEst.dashboardOff', 'Currently hidden on all vehicle pages')}
                                        </span>
                                    </span>
                                </label>
                                <label className="flex items-start gap-2 text-sm cursor-pointer text-slate-300">
                                    <input type="radio" className="mt-1 accent-sky-500" name="payest-override"
                                        checked={payEstOverride !== null}
                                        onChange={() => setPayEstOverride(!settings.payment_estimate_enabled)}
                                        data-testid="cc-manage-payest-override-radio" />
                                    <span>
                                        <span className="font-medium">{t('ccPayEst.overrideOnly', 'Override for this vehicle only')}</span>
                                        <span className="block text-[11px] text-slate-500">
                                            {t('ccPayEst.overrideHint', 'Wins over the dashboard setting for this one vehicle.')}
                                        </span>
                                    </span>
                                </label>
                                {payEstOverride !== null && (
                                    <label className="flex items-center justify-between gap-3 rounded-lg border border-sky-700/60 bg-sky-500/5 px-3 py-2 cursor-pointer ml-6">
                                        <span className="text-xs text-slate-300">
                                            {payEstOverride
                                                ? t('ccPayEst.forceOn', 'Show it on this vehicle')
                                                : t('ccPayEst.forceOff', 'Hide it on this vehicle')}
                                        </span>
                                        <Switch checked={payEstOverride} onCheckedChange={setPayEstOverride}
                                            data-testid="cc-manage-payest-override-switch" />
                                    </label>
                                )}
                            </div>

                            <Button onClick={saveAll} disabled={busy || !bcPhonesOk} className="w-full" data-testid="cc-manage-save">
                                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t('common.save', 'Save')}
                            </Button>
                        </>
                    )}
                </div>
            )}

            <QrCodeModal open={qrOpen} onClose={() => setQrOpen(false)}
                cardId={card?.card_id} isAutomotive />
        </div>
    );
}
